import { FormattedMessage, IntlShape } from "react-intl";
import { Column, Row } from "react-table";
import { OverlayTrigger, Tooltip } from "react-bootstrap";

import { ReactComponent as ArrowDown } from "assets/images/icons/ArrowDown.svg";
import { ReactComponent as ArrowUp } from "assets/images/icons/ArrowUp.svg";
import { ReactComponent as EditIcon } from "assets/images/icons/SubmitIcon.svg";
import { ReactComponent as ResetIcon } from "assets/images/icons/Reset.svg";

interface GoodsRow extends Row {
  original: {
    requested_handling_unit: string;
    requested_units: number;
    requested_w_unit: string;
    requested_weight: number;
    requested_id: number;
    actual_parent_id: number;
    actual_handling_unit: string;
    actual_units: number;
    actual_w_unit: string;
    actual_weight: number;
    actual_id: number;
    actual_updated?: boolean;
    index: number;
  };
}

const getGoodsColumns = (
  intl: IntlShape,
  onEditClick: (commodityIndex: number) => void,
  onRestoreClick: (requestedItemId: number) => void,
  isCustomsInvoice?: boolean,
) => {
  const columns: Column[] = [
    {
      Header: intl.formatMessage({
        id: "quote-request-detail.commodity",
        defaultMessage: "Commodity",
      }),
      accessor: "commodity",
      Cell: ({ row }: { row: GoodsRow }) => {
        if (!row.original) return null;
        const isRemoved = row.original.requested_id && !row.original.actual_id;
        const isNew = !row.original.requested_id;
        // The first condition covers items that are updating during the current form session
        // The second condition covers items that were updated in a previous session
        const isUpdated = (row.original?.actual_updated && !isNew) || row.original?.actual_parent_id;
        return (
          <p className="font-b9">
            {intl.formatMessage({ id: "new-request.packing-list.commodity", defaultMessage: "Commodity" })} {row.index + 1}{" "}
            {isRemoved && (
              <span className="removed-good">{intl.formatMessage({ id: "tabs.removed", defaultMessage: "Removed" })}</span>
            )}
            {isUpdated && (
              <span className="updated-good">
                {intl.formatMessage({ id: "general.updated", defaultMessage: "Updated" })}
              </span>
            )}
            {isNew && <span className="new-good">{intl.formatMessage({ id: "general.new", defaultMessage: "New" })}</span>}
          </p>
        );
      },
    },
    {
      Header: intl.formatMessage({
        id: "billing.invoiceForm.requestedUnits",
        defaultMessage: "Requested units",
      }),
      accessor: "requested_units",
      Cell: ({ row }: { row: GoodsRow }) => {
        const handlingUnit = row.original.requested_handling_unit;
        const numberOfUnits = row.original.requested_units;
        const requestedUnitsString =
          typeof numberOfUnits !== "undefined" && typeof handlingUnit !== "undefined"
            ? `${numberOfUnits} ${handlingUnit}`
            : "-";
        return <span className="text-capitalize">{requestedUnitsString}</span>;
      },
    },
    {
      Header: intl.formatMessage({
        id: "billing.requestedWeight",
        defaultMessage: "Requested weight",
      }),
      accessor: "requested_weight",
      Cell: ({ row }: { row: GoodsRow }) => {
        const weightUnit = row.original.requested_w_unit;
        const weight = row.original.requested_weight;
        const numberOfUnits = row.original.requested_units;
        const requestedWeightString = weight && weightUnit ? `${weight * numberOfUnits} ${weightUnit}` : "-";
        return <span className="text-capitalize">{requestedWeightString}</span>;
      },
    },
    {
      Header: intl.formatMessage({
        id: "billing.invoiceForm.actualUnits",
        defaultMessage: "Actual units",
      }),
      accessor: "actual_units",
      Cell: ({ row }: { row: GoodsRow }) => {
        const handlingUnit = row.original.actual_handling_unit;
        const numberOfUnits = row.original.actual_units;
        const actualUnitsString =
          typeof numberOfUnits !== "undefined" && typeof handlingUnit !== "undefined"
            ? `${numberOfUnits} ${handlingUnit}`
            : "-";
        return <span className="text-capitalize">{actualUnitsString}</span>;
      },
    },
    {
      Header: intl.formatMessage({
        id: "billing.actualWeight",
        defaultMessage: "Actual weight",
      }),
      accessor: "actual_weights",
      Cell: ({ row }: { row: GoodsRow }) => {
        const weightUnit = row.original.actual_w_unit;
        const weight = row.original.actual_weight;
        const numberOfUnits = row.original.actual_units;
        const actualWeightString = weight && weightUnit ? `${weight * numberOfUnits} ${weightUnit}` : "-";
        return <span className="text-capitalize">{actualWeightString}</span>;
      },
    },
    ...(isCustomsInvoice
      ? []
      : [
          {
            Header: intl.formatMessage({ id: "report.action", defaultMessage: "Action" }),
            accessor: "col7",
            sortable: false,
            id: "action",
            Cell: ({ row }: { row: GoodsRow }) => {
              const isRemoved = row.original.requested_id && !row.original.actual_id;
              return (
                <div className="d-flex align-items-center position-relative">
                  {isRemoved && (
                    <OverlayTrigger
                      placement="top"
                      delay={{ show: 250, hide: 0 }}
                      overlay={<Tooltip id="button-tooltip">Restore</Tooltip>}
                    >
                      <ResetIcon
                        onClick={() => onRestoreClick(row.original?.requested_id)}
                        className="mr-2"
                        height="18"
                        width="18"
                        id="restoreIcon"
                      />
                    </OverlayTrigger>
                  )}
                  {!isRemoved && (
                    <OverlayTrigger
                      placement="top"
                      delay={{ show: 250, hide: 0 }}
                      overlay={
                        <Tooltip id="button-tooltip">
                          <FormattedMessage id="general.edit" defaultMessage="Edit" />
                        </Tooltip>
                      }
                    >
                      <EditIcon
                        onClick={() => onEditClick(row.original?.index)}
                        className="mr-2"
                        height="18"
                        width="18"
                        id="editIcon"
                      />
                    </OverlayTrigger>
                  )}
                  {row.isExpanded ? <ArrowUp /> : <ArrowDown />}
                </div>
              );
            },
          },
        ]),
  ];
  return columns;
};

export default getGoodsColumns;
